"use client";
import {
  Box,
  Button,
  Flex,
  Text,
  Image,
  Spinner,
  BoxProps,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { AsyncDisplay, CustomRatioModal } from "components";
import { Lora, Model } from "types";
import { useAppContext } from "hooks";



type SelectionCategory = "style" | "theme" | "outfit" | "hairColor";

const FluxPlaygroundNormal = ({
  prompt,
  setPrompt,
  handleGenerateImage,
  dynamicLoras,
  setDynamicLoras,
  generatedData,
  isGenerating,
  error,
  mt = { base: "0", md: "0" },
  refetchTrigger,
  constantLora,
  model, 
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  handleGenerateImage: (loras: Lora[], ratio: string, numGenerations: number) => void;
  dynamicLoras: Lora[];
  setDynamicLoras: (loras: Lora[]) => void;
  generatedData?: { image_urls: string[] };
  isGenerating: boolean;
  error?: Error | null;
  mt?: BoxProps["mt"];
  refetchTrigger?: () => void;
  constantLora: Lora;
  model: Model | undefined; 
}) => {
  const toast = useToast();
  const [selectedRatio, setSelectedRatio] = useState<string>('portrait_16_9');
  const [numGenerations, setNumGenerations] = useState<number>(1); 
  const [activeRequests, setActiveRequests] = useState<number>(0); 
  const [selections, setSelections] = useState<{
    style: string;
    theme: string;
    outfit: string;
    hairColor: string;
  }>({
    style: "Realistic",
    theme: "City",
    outfit: "Bikini",
    hairColor: "Original",
  });

  
  const [showMore, setShowMore] = useState<Record<SelectionCategory, boolean>>({
    style: false,
    theme: false,
    outfit: false,
    hairColor: false,
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const { profile } = useAppContext();

  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customSize, setCustomSize] = useState<{ width: number; height: number } | null>(null);
  const computedSize = selectedRatio === "custom" && customSize
  ? `${customSize.width}x${customSize.height}`
  : selectedRatio;
  const categories: Record<SelectionCategory, string[]> = {
    style: ["Realistic"],
    theme: [
      "City",
      "Luxury",
      "Vacation",
      "Gamer",
      "Sauna",
      "Snow",
      "Jacuzzi",
      "Christmas",
      "Tennis",
      "Museum",
      "Train Station",
      "Camping",
      "Gym",
      ...(showMore.theme
        ? [
            "Bedroom",
            "Nature",
            "Spa",
            "School",
            "Art Studio",
            "Picnic",
            "Laboratory",
            "Apocalyptic",
            "Rainy day",
            "Bathroom",
            "Mall",
            "Cafe",
            "Strip Club",
            "Kitchen",
            "Airport",
            "Hospital",
            "Botanical Garden",
            "Park",
            "Soccer",
            "Basketball",
            "Farm",
            "Bar",
            "Church",
            "Library",
            "Casino",
            "Beach",
            "Pool",
            "Office",
            "Football",
            "Cave",
            "Mars",
            "Prison",
          ]
        : []),
    ],
    outfit: [
      "Bikini",
      "Lingerie",
      "Robe",
      "Suit",
      "Yoga Pants",
      "Latex",
      "Towel",
      "School uniform",
      "Corset",
      "Dress",
      "Fishnet",
      "Harness",
      "Pantyhose",
      "Pajamas",
      ...(showMore.outfit ? ["Additional Option 1", "Additional Option 2"] : []),
    ],
    hairColor: ["Original", "Blonde", "Ginger", "Dark", "Pink", "Silver"],
  };

  const formatCategoryName = (category: SelectionCategory) => {
    switch (category) {
      case "hairColor":
        return "Hair Color";
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };


  const handleSelection = (category: SelectionCategory, option: string) => {
    setSelections((prev) => {
      // If the current selection is the same as the clicked option, prevent deselection
      if (prev[category] === option) {
        return prev; // Do nothing, as at least one option must remain selected
      }
      return {
        ...prev,
        [category]: option, // Update the selection
      };
    });
  };

  const toggleShowMore = (category: SelectionCategory) => {
    setShowMore((prev) => {
      const updatedShowMore = { ...prev, [category]: !prev[category] };
      // Check if any category is expanded
      setIsExpanded(Object.values(updatedShowMore).some((value) => value));
      return updatedShowMore;
    });
  };

  useEffect(() => {
    // Check if model category is 65e07676582215ba30c8733c (Male model)
    const isMaleModel = model?.model_category?.id === "65e07676582215ba30c8733c";
  
    // Update prompt dynamically based on model category
    const promptParts = [
      selections.style ? `${selections.style.toLowerCase()} photo of ohwx ${isMaleModel ? "man" : "woman"}` : "",
      selections.theme ? `in a ${selections.theme.toLowerCase()} setting` : "",
      selections.outfit ? `wearing ${selections.outfit.toLowerCase()}` : "",
      selections.hairColor ? `with ${selections.hairColor.toLowerCase()} hair color` : "",
    ].filter(Boolean);
  
    setPrompt(promptParts.join(", "));
  }, [selections, setPrompt, model?.model_category?.id]);
  
  const isInsufficientCredits = (profile?.credit ?? 0) < numGenerations;


  const onGenerateClick = () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Prompt cannot be empty. Please describe what you want to see in the image.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 🔒 Check if the user has enough credits
    if ((profile?.credit ?? 0) < numGenerations) {
      toast({
        title: "Insufficient Credits",
        description: `You need at least ${numGenerations} credit${numGenerations > 1 ? 's' : ''} to generate ${numGenerations} image${numGenerations > 1 ? 's' : ''}.`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
  
    const allLoras = [constantLora, ...dynamicLoras];
    // Allow multiple async requests by NOT blocking button usage
    setActiveRequests((prev) => prev + numGenerations);
    handleGenerateImage(allLoras, computedSize, numGenerations);
  };

  const renderCategory = (category: SelectionCategory, options: string[]) => (
    <Box mb={6} key={category}>
      <Text fontSize="16px" fontWeight="bold" color="white" mb={4}>
      {formatCategoryName(category)}
      </Text>
      <Flex gap={2} wrap="wrap">
        {options.map((option) => (
          <Button
            key={option}
            size="sm"
            bg={
              selections[category] === option
                ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                : "transparent"
            }
            color={selections[category] === option ? "black" : "white"}
            _hover={{
              bg:
                selections[category] === option
                  ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                  : "rgba(255, 255, 255, 0.1)",
            }}
            borderRadius="full"
            border={
              selections[category] === option
                ? "none"
                : "1px solid rgba(255, 255, 255, 0.5)"
            }
            onClick={() => handleSelection(category, option)}
          >
            {option}
          </Button>
        ))}
        {category === "theme" && (
          <Button
            size="sm"
            bg="transparent"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            borderRadius="full"
            border="1px solid rgba(255, 255, 255, 0.5)"
            onClick={() => toggleShowMore(category)}
          >
            {showMore[category] ? "Show Less -" : "More +"}
          </Button>
        )}
      </Flex>
    </Box>
  );

  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      w="100%"
      maxW="2420px"
      bg="transparent"
      borderRadius="12px"
      p={{ base: "2px", lg: "16px" }}
      gap={{ base: "12px", lg: "16px" }}
      alignItems="stretch"
      justifyContent="space-between"
      mt={mt}
    >
      {/* Left Box */}
      <Box
        position="relative"
        w={{ base: "100%", lg: "49%" }}
        h={{ base: "auto", md: "auto" }}
        bg="#000000"
        borderRadius="20px"
        display="flex"
        flexDirection="column"
        p={8}
        overflowY="auto"
        border="1px solid rgba(255, 255, 255, 0.5)"
        _focus={{ outline: "none", boxShadow: "none" }}
        css={{
          "::-webkit-scrollbar": { width: "8px" },
          "::-webkit-scrollbar-thumb": { background: "#555", borderRadius: "8px" },
        }}
      >
        {Object.entries(categories).map(([category, options]) =>
          renderCategory(category as SelectionCategory, options)
        )}


        <Flex
          w="100%"
          align="start"
          direction="column"// Adjusted to match the spacing between Hair Color and its options
          mb={6} // Maintains consistent spacing below
        >
          <Text color="white" fontSize="16px" fontWeight="bold" mb={4}>
            Ratio
          </Text>
          <Flex gap={3} flexWrap="wrap">
            {[
              { key: "portrait_16_9", label: "Portrait" },
              { key: "square_hd", label: "Square" },
              { key: "landscape_16_9", label: "Landscape" },
            ].map((option) => (
              <Button
                key={option.key}
                size="sm"
                bg={
                  selectedRatio === option.key
                    ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    : "transparent"
                }
                color={selectedRatio === option.key ? "black" : "white"}
                _hover={{
                  bg:
                    selectedRatio === option.key
                      ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                      : "rgba(255, 255, 255, 0.1)",
                }}
                borderRadius="full"
                border={
                  selectedRatio === option.key
                    ? "none"
                    : "1px solid rgba(255, 255, 255, 0.5)"
                }
                onClick={() => setSelectedRatio(option.key)}
              >
                {option.label}
              </Button>
              
            ))}
            <Button
              size="sm"
              onClick={() => setIsCustomModalOpen(true)}
              bg={
                selectedRatio === "custom"
                  ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                  : "transparent"
              }
              color={selectedRatio === "custom" ? "black" : "white"}
              _hover={{
                bg:
                  selectedRatio === "custom"
                    ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                    : "rgba(255, 255, 255, 0.1)",
              }}
              borderRadius="full"
              border={
                selectedRatio === "custom"
                  ? "none"
                  : "1px solid rgba(255, 255, 255, 0.5)"
              }
            >
              Custom
            </Button>
          </Flex>
          <Box mt={4}>
          <Text color="white" fontSize="16px" fontWeight="bold" mb={4}>
            Number of Generations
          </Text>
          <Flex gap={3}>
            {[1, 2, 3, 4].map((count) => (
              <Button
                key={count}
                size="sm"
                bg={numGenerations === count ? "linear-gradient(to right, #FFB5FF, #1AA5D3)" : "transparent"}
                color={numGenerations === count ? "black" : "white"}
                _hover={{
                  bg:
                    numGenerations === count
                      ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                      : "rgba(255, 255, 255, 0.1)",
                }}
                borderRadius="full"
                border={
                  numGenerations === count
                    ? "none"
                    : "1px solid rgba(255, 255, 255, 0.5)"
                }
                onClick={() => setNumGenerations(count)}
              >
                {count}
              </Button>
            ))}
          </Flex>
        </Box>
        </Flex>




        <Flex direction="column" alignItems="center" mt={32}>
        <Button
  w="100%"
  h="50px"
  bg={isInsufficientCredits ? "gray.600" : "#FF1B6D"}
  color="white"
  borderRadius="6px"
  fontWeight="bold"
  onClick={onGenerateClick}
  isDisabled={isInsufficientCredits}
  outline="none"
  _focus={{ outline: "none", boxShadow: "none" }}
  mt={12}
>
  {isInsufficientCredits
    ? "Insufficient Credits"
    : `Create (${numGenerations} credit${numGenerations > 1 ? "s" : ""})`}
</Button>

        </Flex>

        {error && <Text color="red.500">Error: {error.message}</Text>}
      </Box>
      {/* Custom Ratio Modal */}
      <CustomRatioModal
          isOpen={isCustomModalOpen}
          onClose={() => {
            setIsCustomModalOpen(false);
            // Only reset if user hadn't confirmed Save
            if (selectedRatio === "custom" && !customSize) {
              setSelectedRatio("portrait_16_9");
            }
          }}
          onSave={(width, height) => {
            setCustomSize({ width, height });
            setSelectedRatio("custom");
            setIsCustomModalOpen(false);
          }}
        />

      {/* Right Box */}
      <Box
        w={{ base: "100%", lg: "49%" }}
        h={{ base: "auto", md: "auto" }}
        bg="#000000"
        borderRadius="20px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        border="1px solid rgba(255, 255, 255, 0.5)"
        boxShadow="0px 0px 0px 2px linear-gradient(to right, #FFE1FF 30%, #A4DBEE 100%)"
      >
         {isGenerating ? (
            <AsyncDisplay modelId={model?.id || ""} isGenerating={activeRequests > 0} generatedData={generatedData}   numGenerations={numGenerations}   activeRequests={activeRequests}
            setActiveRequests={setActiveRequests}/>
          ) : (
            <AsyncDisplay modelId={model?.id || ""} isGenerating={activeRequests > 0} generatedData={generatedData}   numGenerations={numGenerations}   activeRequests={activeRequests}
            setActiveRequests={setActiveRequests}/>
          )}
      </Box>
    </Flex>
  );
};

export default FluxPlaygroundNormal;
